import { Injectable, Injector, Type, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';

import { DialogRef } from './dialog-ref';
import {
  ConfirmOptions,
  DialogAction,
  DialogConfig,
  DialogInstance,
  DialogResult,
  contentOf,
} from './dialog.models';
import { DIALOG_CONFIG, DIALOG_DATA } from './dialog.tokens';

/**
 * The multi-dialog stack. One signal array of instances; <app-dialog-host>
 * renders it. No CDK, no per-component overlay: a dialog opening another
 * dialog just injects this service and consumes the child's afterClosed().
 *
 * BINDING CONTRACT - COMMON_COMPONENTS.md section 3, "Multi-dialog stacking".
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly parentInjector = inject(Injector);
  private readonly router = inject(Router, { optional: true });

  private readonly instances = signal<readonly DialogInstance[]>([]);
  private sequence = 0;

  /** Open dialogs, bottom-first. The host iterates this. */
  readonly stack = this.instances.asReadonly();

  /** Drives the body scroll lock. */
  readonly hasOpen = computed(() => this.instances().length > 0);

  /** The id of the top-most instance, or null. Drives aria-modal and the focus trap. */
  readonly topId = computed<string | null>(() => {
    const open = this.instances();
    return open.length ? open[open.length - 1].id : null;
  });

  constructor() {
    // A route change must never leave an orphaned overlay on screen. The
    // logout path calls closeAll() directly for the same reason.
    this.router?.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => this.closeAll());
  }

  /**
   * Mints an id, builds a child Injector carrying DialogRef / DIALOG_DATA /
   * DIALOG_CONFIG, appends the instance and returns the ref. Each instance
   * gets its OWN injector, which is what makes two simultaneously open
   * dialogs unable to see each other's data.
   */
  open<R = unknown, TData = unknown>(
    component: Type<unknown>,
    config: DialogConfig<TData> = {}
  ): DialogRef<R> {
    const id = `dlg-${++this.sequence}`;
    const ref = new DialogRef<R>(id, config.title ?? '', (settled) => this.remove(settled.id));

    const injector = Injector.create({
      name: id,
      parent: this.parentInjector,
      providers: [
        { provide: DialogRef, useValue: ref },
        { provide: DIALOG_DATA, useValue: config.data ?? null },
        { provide: DIALOG_CONFIG, useValue: config as DialogConfig },
      ],
    });

    const instance: DialogInstance<TData, R> = { id, component, config, ref, injector };
    this.instances.update((open) => [...open, instance as DialogInstance]);
    return ref;
  }

  /**
   * Closes the top instance only, honouring its closeOnEsc / canClose().
   * Esc can therefore never reach a dialog buried under another one.
   * Resolves true when the dialog actually closed.
   */
  async closeTop(action: DialogAction = 'close'): Promise<boolean> {
    const open = this.instances();
    if (!open.length) return false;
    const top = open[open.length - 1];
    if (action === 'esc' && top.config.closeOnEsc === false) return false;
    if (action === 'backdrop' && top.config.closeOnBackdrop !== true) return false;
    return this.requestClose(top, action);
  }

  /** Backdrop / header-close / footer paths funnel through here for the canClose() veto. */
  async requestClose(instance: DialogInstance, action: DialogAction): Promise<boolean> {
    const content = contentOf(instance.ref.contentRef);
    if (typeof content?.canClose === 'function') {
      const permitted = await content.canClose();
      if (!permitted) return false;
    }
    instance.ref.close({ action });
    return true;
  }

  /** Empties the stack and completes every ref. Ignores canClose() by design. */
  closeAll(action: DialogAction = 'close'): void {
    const open = this.instances();
    this.instances.set([]);
    // Snapshot first: close() calls back into remove(), and iterating the live
    // signal value while it shrinks would skip instances.
    for (const instance of open) instance.ref.close({ action });
  }

  /** True only when the confirm dialog resolved with the 'save' action. */
  async confirm(options: ConfirmOptions): Promise<boolean> {
    // Dynamic import: core must not statically depend on shared/components
    // (emg-project-rules section 5, dependency direction), and it keeps the
    // confirm dialog out of the initial bundle.
    const { ConfirmDialog } = await import('@shared/components/confirm-dialog/confirm-dialog');
    const ref = this.openConfirm(ConfirmDialog, options);
    const result: DialogResult<void> = await firstValueFrom(ref.afterClosed());
    return result.action === 'save';
  }

  /** Split out so a spec can supply a stub confirm component. */
  openConfirm(component: Type<unknown>, options: ConfirmOptions): DialogRef<void> {
    const danger = options.tone === 'danger';
    return this.open<void, ConfirmOptions>(component, {
      title: options.title,
      titleAccent: options.titleAccent,
      data: options,
      size: 'sm',
      saveLabel: options.confirmLabel ?? (danger ? 'Delete' : 'Confirm'),
      cancelLabel: options.cancelLabel ?? 'Cancel',
      disableSaveWhenInvalid: false,
      closeOnEsc: true,
      closeOnBackdrop: true,
      panelClass: danger ? 'dlg-danger' : undefined,
    });
  }

  private remove(id: string): void {
    this.instances.update((open) => open.filter((instance) => instance.id !== id));
  }
}
